import { useState, useMemo } from 'react'
import { Check, Columns2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog'
import type { Photo, ReferencePhoto } from '@/types/visit'

function formatTag(tag: string): string {
  return tag
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function formatDateTime(dateString: string): string {
  const normalized = dateString.includes('T') ? dateString : `${dateString}T00:00:00`
  return new Date(normalized).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/** Unified photo shape for rendering */
interface GalleryPhoto {
  id: string
  url: string
  thumbnail_url: string
  tag: string
  visitDate: string
  visitLabel: string
}

interface PhotoGalleryProps {
  photos: Photo[]
  referencePhotos: ReferencePhoto[]
  currentVisitDate: string
}

export default function PhotoGallery({
  photos,
  referencePhotos,
  currentVisitDate,
}: PhotoGalleryProps) {
  const [selected, setSelected] = useState<GalleryPhoto[]>([])
  const [compareOpen, setCompareOpen] = useState(false)

  // Group all photos by visit date, newest first
  const visitGroups = useMemo(() => {
    const currentPhotos: GalleryPhoto[] = photos.map((p) => ({
      id: p.id,
      url: p.url,
      thumbnail_url: p.thumbnail_url,
      tag: p.tag,
      visitDate: currentVisitDate,
      visitLabel: 'Current Visit',
    }))

    // Group reference photos by visit_date
    const refByDate = new Map<string, GalleryPhoto[]>()
    for (const rp of referencePhotos) {
      const dateKey = rp.visit_date
      if (!refByDate.has(dateKey)) {
        refByDate.set(dateKey, [])
      }
      refByDate.get(dateKey)!.push({
        id: rp.id,
        url: rp.url,
        thumbnail_url: rp.thumbnail_url,
        tag: rp.tag,
        visitDate: dateKey,
        visitLabel: formatDateTime(dateKey),
      })
    }

    const groups: { date: string; label: string; photos: GalleryPhoto[] }[] = []

    // Current visit first (newest)
    if (currentPhotos.length > 0) {
      groups.push({
        date: currentVisitDate,
        label: `Current Visit — ${formatDateTime(currentVisitDate)}`,
        photos: currentPhotos,
      })
    }

    // Previous visits sorted newest first
    const sortedDates = [...refByDate.keys()].sort(
      (a, b) => new Date(b).getTime() - new Date(a).getTime(),
    )
    for (const date of sortedDates) {
      const groupPhotos = refByDate.get(date)!
      groups.push({
        date,
        label: formatDateTime(date),
        photos: groupPhotos,
      })
    }

    return groups
  }, [photos, referencePhotos, currentVisitDate])

  const isSelected = (photo: GalleryPhoto) =>
    selected.some((s) => s.id === photo.id)

  const toggleSelect = (photo: GalleryPhoto) => {
    if (isSelected(photo)) {
      setSelected((prev) => prev.filter((s) => s.id !== photo.id))
    } else {
      setSelected((prev) => [...prev, photo])
    }
  }

  const clearSelection = () => setSelected([])

  const totalPhotos = visitGroups.reduce((sum, g) => sum + g.photos.length, 0)

  if (totalPhotos === 0) {
    return (
      <div className="rounded-lg border bg-white p-6">
        <h2 className="text-lg font-medium text-slate-900">Photos</h2>
        <p className="mt-4 text-center text-sm text-slate-500">No photos uploaded.</p>
      </div>
    )
  }

  return (
    <>
      {/* Selection toolbar */}
      {selected.length > 0 && (
        <div className="sticky top-0 z-10 flex items-center gap-3 rounded-lg border bg-white px-4 py-3 shadow-xs">
          <span className="text-sm font-medium text-slate-700">
            {selected.length} photo{selected.length !== 1 ? 's' : ''} selected
          </span>
          <Button
            size="sm"
            onClick={() => setCompareOpen(true)}
            disabled={selected.length < 2}
          >
            <Columns2 className="h-4 w-4 mr-1" />
            Compare
          </Button>
          <Button size="sm" variant="ghost" onClick={clearSelection}>
            Clear
          </Button>
        </div>
      )}

      {/* Visit groups */}
      <div className="space-y-6">
        {visitGroups.map((group) => (
          <div key={group.date} className="rounded-lg border bg-white p-6">
            <h3 className="mb-4 text-sm font-semibold text-slate-900">
              {group.label}
            </h3>
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-5">
              {group.photos.map((photo) => {
                const photoSelected = isSelected(photo)
                return (
                  <button
                    key={photo.id}
                    onClick={() => toggleSelect(photo)}
                    className={`group relative aspect-square overflow-hidden rounded-lg border bg-slate-100 focus:outline-hidden focus:ring-2 focus:ring-primary-500 ${
                      photoSelected ? 'ring-2 ring-primary-500' : ''
                    }`}
                  >
                    <img
                      src={photo.thumbnail_url}
                      alt={`${formatTag(photo.tag)} photo`}
                      className="h-full w-full object-cover transition-transform group-hover:scale-105"
                    />
                    <Badge
                      variant="secondary"
                      className="absolute bottom-1.5 left-1.5 text-xs"
                    >
                      {formatTag(photo.tag)}
                    </Badge>
                    {photoSelected && (
                      <div className="absolute top-1.5 right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-white">
                        <Check className="h-3 w-3" />
                      </div>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Comparison modal */}
      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <DialogContent className="max-w-5xl p-0">
          <DialogTitle className="sr-only">Photo Comparison</DialogTitle>
          <div className="flex flex-col">
            <div className="border-b px-4 py-3">
              <h2 className="text-sm font-semibold text-slate-900">
                Comparing {selected.length} Photos
              </h2>
            </div>
            <div
              className="grid gap-4 p-4"
              style={{
                gridTemplateColumns: `repeat(${Math.min(selected.length, 3)}, 1fr)`,
              }}
            >
              {selected.map((photo) => (
                <div key={photo.id} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-slate-500">
                      {photo.visitLabel}
                    </span>
                    <Badge variant="secondary" className="text-xs">
                      {formatTag(photo.tag)}
                    </Badge>
                  </div>
                  <div className="aspect-square overflow-hidden rounded-lg border bg-slate-100">
                    <img
                      src={photo.url}
                      alt={`${formatTag(photo.tag)} — ${photo.visitLabel}`}
                      className="h-full w-full object-cover"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
